import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { pushSupported } from "@/lib/push";
import { isStandalone } from "@/lib/pwa";

// « Nouveauté » notifications for visitors (no account, no personal data): endpoint + keys + language only.
const FLAG = "fmo_news_push";
const VISITS = "fmo_visits";
const PROMPTED = "fmo_news_prompted";
const EVENT = "fmo:news-push";

const b64ToBytes = (b64) => {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

export const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
// iPhone/iPad: Web Push only from the home-screen app (iOS 16.4+).
export const needsHomeScreen = () => isIos() && !isStandalone();

export async function newsSubscribe(lang) {
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("denied");
  const { data } = await api.get("/public/push/key");
  const reg = await navigator.serviceWorker.ready;
  const sub = (await reg.pushManager.getSubscription()) || (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToBytes(data.public_key) }));
  await api.post("/public/push/subscribe", { ...sub.toJSON(), lang });
  localStorage.setItem(FLAG, "1");
  localStorage.setItem("fmo_news_lang", lang);
  window.dispatchEvent(new Event(EVENT));
}

export async function newsUnsubscribe() {
  const sub = await (await navigator.serviceWorker.ready).pushManager.getSubscription();
  if (sub) await api.post("/public/push/unsubscribe", { endpoint: sub.endpoint });
  localStorage.removeItem(FLAG);
  window.dispatchEvent(new Event(EVENT));
}

export function useNewsPush() {
  const read = () => localStorage.getItem(FLAG) === "1" && pushSupported() && Notification.permission === "granted";
  const [on, setOn] = useState(read);
  const sync = useCallback(() => setOn(read()), []);
  useEffect(() => {
    window.addEventListener(EVENT, sync);
    return () => window.removeEventListener(EVENT, sync);
  }, [sync]);
  return { on, supported: pushSupported(), denied: pushSupported() && Notification.permission === "denied" };
}

// One count per browser session; the soft prompt is offered once, from the 2nd visit (never during the very first visit).
export function shouldSoftPrompt() {
  try {
    if (!sessionStorage.getItem(VISITS)) {
      sessionStorage.setItem(VISITS, "1");
      localStorage.setItem(VISITS, String(Number(localStorage.getItem(VISITS) || 0) + 1));
    }
    return Number(localStorage.getItem(VISITS) || 0) >= 2 && !localStorage.getItem(PROMPTED) && localStorage.getItem(FLAG) !== "1";
  } catch {
    return false;
  }
}

export const markPrompted = () => localStorage.setItem(PROMPTED, "1");

// Language changed while subscribed: the next notifications follow it.
export async function newsRelang(lang) {
  if (localStorage.getItem(FLAG) !== "1" || !pushSupported() || localStorage.getItem("fmo_news_lang") === lang) return;
  localStorage.setItem("fmo_news_lang", lang);
  const sub = await (await navigator.serviceWorker.ready).pushManager.getSubscription();
  if (sub) await api.post("/public/push/subscribe", { ...sub.toJSON(), lang }).catch(() => {});
}
